import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import API from "../lib/api";
import { useAuth } from "./AuthContext";

/** Engineer opt-in live position sharing. Lives above the router so the phone keeps reporting while they move
 *  around the app. Switched on from the My Work card; the choice is remembered (localStorage) and resumes on
 *  reopen. Sends a fix every ~60 s, or sooner when they've moved more than 50 m; switching off wipes the stored
 *  position server-side. */
const KEY_PREFIX = "fixli_share_location:"; // per user — another engineer on the same phone must opt in themselves
const SEND_EVERY_MS = 60_000;
const MIN_MOVE_M = 50;
const SHARE_ROLES = ["in_house", "vendor_operative"];

const Ctx = createContext(null);

const distM = (a, b) => {
  const R = 6371000, toR = (d) => (d * Math.PI) / 180;
  const dLat = toR(b.lat - a.lat), dLng = toR(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

export function LiveLocationProvider({ children }) {
  const { user } = useAuth();
  const uid = user?.id || user?._id || "";
  const eligible = SHARE_ROLES.includes(user?.role);
  const key = uid ? KEY_PREFIX + uid : "";
  const [sharing, setSharing] = useState(false);
  // resume the signed-in engineer's own choice; a different (or no) user never inherits it
  useEffect(() => { setSharing(!!key && localStorage.getItem(key) === "1"); }, [key]);
  const [status, setStatus] = useState("off"); // off | locating | sharing | error
  const [error, setError] = useState("");
  const [lastFix, setLastFix] = useState(null); // {lat, lng, accuracy, sentAt}
  const watchRef = useRef(null);
  const lastSentRef = useRef(null);
  const timerRef = useRef(null);
  const pendingRef = useRef(null);

  const send = useCallback(async (fix) => {
    try {
      await API.put("/in-house-portal/live-location", { latitude: fix.lat, longitude: fix.lng, accuracy_m: fix.accuracy });
      lastSentRef.current = { ...fix, sentAt: Date.now() };
      setLastFix(lastSentRef.current);
      setStatus("sharing");
      setError("");
    } catch (e) {
      setStatus("error");
      setError(e.response?.data?.detail || "Couldn't send your position — will retry");
    }
  }, []);

  const stopWatching = useCallback(() => {
    if (watchRef.current != null && navigator.geolocation) navigator.geolocation.clearWatch(watchRef.current);
    watchRef.current = null;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    pendingRef.current = null;
  }, []);

  useEffect(() => {
    if (!eligible || !sharing) { stopWatching(); if (!sharing) setStatus("off"); return undefined; }
    if (!navigator.geolocation) { setStatus("error"); setError("This device can't share its location"); return undefined; }
    setStatus((s) => (s === "sharing" ? s : "locating"));
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const fix = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
        pendingRef.current = fix;
        const last = lastSentRef.current;
        if (!last || Date.now() - last.sentAt >= SEND_EVERY_MS || distM(last, fix) >= MIN_MOVE_M) send(fix);
      },
      (err) => {
        setStatus("error");
        setError(err.code === 1 ? "Location permission denied — allow location for this site to share" : "Couldn't get a GPS fix yet");
      },
      { enableHighAccuracy: true, maximumAge: 15_000, timeout: 30_000 },
    );
    // heartbeat: even when standing still, keep the office's copy fresh (the ranking treats >15 min as stale)
    timerRef.current = setInterval(() => {
      const fix = pendingRef.current;
      if (fix && (!lastSentRef.current || Date.now() - lastSentRef.current.sentAt >= SEND_EVERY_MS - 1000)) send(fix);
    }, SEND_EVERY_MS);
    return stopWatching;
  }, [eligible, sharing, send, stopWatching]);

  const start = useCallback(() => { if (key) localStorage.setItem(key, "1"); setError(""); setSharing(true); }, [key]);
  const stop = useCallback(async () => {
    if (key) localStorage.removeItem(key);
    setSharing(false);
    stopWatching();
    lastSentRef.current = null;
    setLastFix(null);
    setStatus("off");
    try { await API.delete("/in-house-portal/live-location"); } catch { /* best effort */ }
  }, [key, stopWatching]);

  // logging out ends the session's sharing and wipes the stored position while the token is still valid; the
  // engineer's choice is kept, so it resumes when they sign in again on this device
  useEffect(() => {
    const onLogout = () => {
      if (!sharing) return;
      stopWatching();
      lastSentRef.current = null;
      setLastFix(null);
      setStatus("off");
      setSharing(false);
      API.delete("/in-house-portal/live-location").catch(() => {});
    };
    window.addEventListener("fixli:before-logout", onLogout);
    return () => window.removeEventListener("fixli:before-logout", onLogout);
  }, [sharing, stopWatching]);

  const value = useMemo(() => ({ eligible, sharing: eligible && sharing, status, error, lastFix, start, stop }), [eligible, sharing, status, error, lastFix, start, stop]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useLiveLocation = () => useContext(Ctx) || { eligible: false, sharing: false, status: "off", error: "", lastFix: null, start: () => {}, stop: () => {} };
